import React, { useCallback, useMemo, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { ColorPicker as AntColorPicker, ConfigProvider } from "antd";
import type { ColorPickerProps as AntColorPickerProps } from "antd";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import { Label } from "../label";
import type { TooltipPlacement, TooltipVariant } from "../tool-tip";

type AntColorValue = Parameters<
  NonNullable<AntColorPickerProps["onChange"]>
>[0];

export type ColorPickerSize = "small" | "medium" | "large";
export type ColorPickerVariant = "outlined" | "filled" | "borderless";
export type ColorPickerValue = string | null;

export interface ColorPickerProps
  extends Omit<
    AntColorPickerProps,
    | "children"
    | "className"
    | "defaultValue"
    | "disabled"
    | "onChange"
    | "onClear"
    | "size"
    | "style"
    | "value"
  > {
  label?: React.ReactNode;
  required?: boolean;
  helperText?: React.ReactNode;
  error?: React.ReactNode;
  tooltip?: React.ReactNode;
  tooltipPlacement?: TooltipPlacement;
  tooltipVariant?: TooltipVariant;
  size?: ColorPickerSize;
  variant?: ColorPickerVariant;
  fullWidth?: boolean;
  disabled?: boolean;
  value?: ColorPickerValue;
  defaultValue?: ColorPickerValue;
  placeholder?: string;
  showValue?: boolean;
  showClear?: boolean;
  className?: string;
  style?: React.CSSProperties;
  triggerClassName?: string;
  triggerStyle?: React.CSSProperties;
  swatchClassName?: string;
  swatchStyle?: React.CSSProperties;
  valueClassName?: string;
  valueStyle?: React.CSSProperties;
  /** Optional icon displayed at the end of the picker trigger. */
  suffixIcon?: React.ReactNode;
  onChange?: (value: ColorPickerValue, color: AntColorValue | null) => void;
  onClear?: () => void;
  /** Keep hue/saturation while editing colors whose hex value loses that information. */
  preserveColorSpace?: boolean;
}

interface HelperTextProps {
  children: React.ReactNode;
  isError?: boolean;
}

const sizeConfig: Record<
  ColorPickerSize,
  {
    height: number;
    swatch: number;
    fontSize: number;
    icon: number;
    paddingX: string;
  }
> = {
  small: {
    height: 32,
    swatch: 18,
    fontSize: 13,
    icon: 14,
    paddingX: "px-2.5",
  },
  medium: {
    height: 40,
    swatch: 22,
    fontSize: 14,
    icon: 16,
    paddingX: "px-3",
  },
  large: {
    height: 48,
    swatch: 26,
    fontSize: 15,
    icon: 18,
    paddingX: "px-4",
  },
};

const DEFAULT_COLOR = colors.primary;
const EMPTY_COLOR_LABEL = "No color selected";
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const HelperText = ({ children, isError }: HelperTextProps) => (
  <div
    className="mt-1 text-xs"
    style={{ color: isError ? colors.error : colors.textMuted }}
  >
    {children}
  </div>
);

const normalizeColorString = (value: ColorPickerValue | undefined) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const isColorSupported = (value: string) => {
  if (HEX_COLOR_REGEX.test(value)) return true;

  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", value);
  }

  return /^(rgb|rgba|hsl|hsla|hsb|hsba)\(/i.test(value);
};

const toDisplayColor = (value: ColorPickerValue | undefined) => {
  const normalized = normalizeColorString(value);
  if (!normalized || !isColorSupported(normalized)) return null;
  return normalized;
};

const getColorText = (value: ColorPickerValue | undefined) =>
  normalizeColorString(value)?.toUpperCase() ?? EMPTY_COLOR_LABEL;

const getHexColorValue = (color: AntColorValue) => {
  try {
    return color.toHexString().toUpperCase();
  } catch {
    return null;
  }
};

export const ColorPicker = React.forwardRef<HTMLDivElement, ColorPickerProps>(
  (
    {
      label,
      required = false,
      helperText,
      error,
      tooltip,
      tooltipPlacement = "top",
      tooltipVariant = "default",
      size = "medium",
      variant = "filled",
      fullWidth = true,
      disabled = false,
      value,
      defaultValue = DEFAULT_COLOR,
      placeholder = EMPTY_COLOR_LABEL,
      showValue = true,
      showClear = true,
      allowClear = true,
      showText = false,
      format = "hex",
      className,
      style,
      triggerClassName,
      triggerStyle,
      swatchClassName,
      swatchStyle,
      valueClassName,
      valueStyle,
      suffixIcon,
      onChange,
      onClear,
      onOpenChange,
      preserveColorSpace = false,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState<ColorPickerValue>(
      normalizeColorString(defaultValue),
    );
    const [isOpen, setIsOpen] = useState(false);
    const [pickerDraft, setPickerDraft] = useState<{ hex: string; color: AntColorValue } | null>(null);

    const isControlled = value !== undefined;
    const selectedValue = isControlled ? normalizeColorString(value) : internalValue;
    const displayColor = toDisplayColor(selectedValue);
    const hasValue = Boolean(selectedValue);
    const hasError = Boolean(error);
    const config = sizeConfig[size];

    const triggerBorderColor = useMemo(() => {
      if (variant === "borderless") return colors.transparent;
      if (hasError) return colors.error;
      if (isOpen) return colors.primary;
      if (hasValue) return colors.surfaceDark;
      return colors.surfaceSubtle;
    }, [hasError, hasValue, isOpen, variant]);

    const triggerBackgroundColor = useMemo(() => {
      if (variant === "borderless") return colors.transparent;
      if (disabled) return colors.surfaceLighter;
      if (isOpen) return colors.white;
      return variant === "filled" ? colors.surfaceLight : colors.white;
    }, [disabled, isOpen, variant]);

    const updateValue = useCallback(
      (nextValue: ColorPickerValue, nextColor: AntColorValue | null) => {
        if (preserveColorSpace) setPickerDraft(nextValue && nextColor ? { hex: nextValue, color: nextColor } : null);
        if (!isControlled) setInternalValue(nextValue);
        onChange?.(nextValue, nextColor);
      },
      [isControlled, onChange, preserveColorSpace],
    );

    const handleChange = useCallback(
      (nextColor: AntColorValue) => {
        const nextValue = getHexColorValue(nextColor);
        updateValue(nextValue, nextColor);
      },
      [updateValue],
    );

    const handleClear = useCallback(
      (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        updateValue(null, null);
        onClear?.();
      },
      [onClear, updateValue],
    );

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        setIsOpen(nextOpen);
        onOpenChange?.(nextOpen);
      },
      [onOpenChange],
    );

    return (
      <div
        ref={ref}
        className={cn(fullWidth ? "w-full" : "inline-flex", className)}
        style={style}
      >
        {label && (
          <Label
            required={required}
            disabled={disabled}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            tooltipVariant={tooltipVariant}
          >
            {label}
          </Label>
        )}

        <ConfigProvider
          theme={{
            token: {
              colorPrimary: colors.primary,
              colorError: colors.error,
              colorText: colors.textDefault,
              colorTextDisabled: colors.textWeak,
              colorTextPlaceholder: colors.textMuted,
              colorBorder: colors.surfaceSubtle,
              colorBgContainer: colors.white,
              borderRadius: 3,
            },
          }}
        >
          <AntColorPicker
            value={preserveColorSpace && pickerDraft && pickerDraft.hex.toLowerCase() === selectedValue?.toLowerCase() ? pickerDraft.color : selectedValue}
            defaultValue={undefined}
            disabled={disabled}
            allowClear={allowClear}
            format={format}
            showText={showText}
            onChange={handleChange}
            onClear={() => handleClear()}
            onOpenChange={handleOpenChange}
            {...props}
          >
            <button
              type="button"
              disabled={disabled}
              aria-invalid={hasError || undefined}
              aria-label={typeof label === "string" ? label : "Color picker"}
              className={cn(
                "flex min-w-0 items-center gap-2 rounded-[3px] border text-left transition-colors",
                config.paddingX,
                fullWidth ? "w-full" : "inline-flex",
                disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                triggerClassName,
              )}
              style={{
                minHeight: config.height,
                borderColor: triggerBorderColor,
                borderWidth: isOpen || hasError ? 2 : 1,
                backgroundColor: triggerBackgroundColor,
                ...triggerStyle,
              }}
            >
              <span
                className={cn(
                  "inline-flex shrink-0 items-center justify-center rounded-[3px]",
                  swatchClassName,
                )}
                style={{
                  width: config.swatch,
                  height: config.swatch,
                  backgroundColor: displayColor ?? colors.white,
                  border: `1px solid ${colors.surface}`,
                  ...swatchStyle,
                }}
              >
                {displayColor && (
                  <Check size={Math.max(12, config.icon - 2)} color={colors.white} />
                )}
              </span>

              {showValue && (
                <span
                  className={cn("min-w-0 flex-1 truncate", valueClassName)}
                  style={{
                    color: hasValue ? colors.textDefault : colors.textMuted,
                    fontSize: config.fontSize,
                    ...valueStyle,
                  }}
                >
                  {hasValue ? getColorText(selectedValue) : placeholder}
                </span>
              )}

              {showClear && hasValue && !disabled ? (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear color"
                  className="inline-flex shrink-0 items-center justify-center"
                  style={{ color: colors.textMuted }}
                  onClick={(event) => handleClear(event)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handleClear();
                    }
                  }}
                >
                  <X size={config.icon} />
                </span>
              ) : suffixIcon ? (
                <span
                  className="inline-flex shrink-0 items-center justify-center"
                  style={{ color: colors.textMuted }}
                  aria-hidden="true"
                >
                  {suffixIcon}
                </span>
              ) : (
                <ChevronDown
                  size={config.icon}
                  className="shrink-0"
                  style={{ color: colors.textMuted }}
                />
              )}
            </button>
          </AntColorPicker>
        </ConfigProvider>

        {helperText && !error && <HelperText>{helperText}</HelperText>}
        {error && <HelperText isError>{error}</HelperText>}
      </div>
    );
  },
);

ColorPicker.displayName = "ColorPicker";

export default ColorPicker;
